import React, { FC, Fragment, useState } from 'react';
import { formatDate } from 'utils/format';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { MillingJob, MillingJobStatus } from 'models/milling';
import MillingJobStatusChip from 'components/shared/milling-job-status-chip/milling-job-status-chip.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useTranslation } from 'react-i18next';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import StlMultiPreviewList from 'components/shared/stl-multi-preview-list/stl-multi-preview-list.component';

type MillingJobRowProps = {
    job: MillingJob;
    onDeleteClick: (jobId: string) => void;
    onCreateWarrantyClick: (jobId: string) => void;
};
const MillingJobRow: FC<MillingJobRowProps> = ({ job, onDeleteClick, onCreateWarrantyClick }: MillingJobRowProps) => {
    const { t: translate } = useTranslation(['milling-jobs']);
    const [deleteJobConfirmDialogOpen, setDeleteJobConfirmDialogOpen] = useState(false);
    const [warrantyConfirmDialogOpen, setWarrantyConfirmDialogOpen] = useState(false);
    const [jobId, setJobId] = useState('');

    const handleDeleteClick = (jobId: string) => {
        setJobId(jobId as string);
        setDeleteJobConfirmDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        onDeleteClick(jobId as string);
        setDeleteJobConfirmDialogOpen(false);
        setJobId('');
    };

    const handleCloseDialog = () => {
        setDeleteJobConfirmDialogOpen(false);
        setWarrantyConfirmDialogOpen(false);
        setJobId('');
    };

    const handleCreateWarrantyClick = (jobId: string) => {
        setJobId(jobId as string);
        setWarrantyConfirmDialogOpen(true);
    };

    const handleConfirmCreateWarranty = () => {
        onCreateWarrantyClick(jobId as string);
        setWarrantyConfirmDialogOpen(false);
        setJobId('');
    };

    return (
        <Fragment>
            <TableRow>
                <TableCell>
                    <Box>
                        {job.jobType}: {job.material} {job.color}
                    </Box>
                    <MillingJobStatusChip job={job} />
                </TableCell>
                <TableCell>{job.filename}</TableCell>
                <TableCell>
                    {job.order.internalOrderId ? job.order.internalOrderId : translate('orders:NO_INTERNAL_ORDER_ID')}
                </TableCell>
                <TableCell>
                    {job.deadline ? formatDate(job.deadline, 'date') : translate('milling-jobs:NO_DEADLINE')}
                </TableCell>
                <TableCell>
                    <Box>{formatDate(job.createdAt, 'datetime')}</Box>
                    <Typography variant="caption">{job.createdBy.fullName}</Typography>
                </TableCell>
                <TableCell></TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {job.comment && (
                            <Tooltip title={job.comment} placement="top">
                                <IconButton>
                                    <CommentOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {job.status === MillingJobStatus.FINISHED && (
                            <Box>
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        handleCreateWarrantyClick(job._id as string);
                                    }}
                                >
                                    <VerifiedUserIcon />
                                </IconButton>
                            </Box>
                        )}
                        {job.stlFiles?.length > 0 && (
                            <Box>
                                <StlMultiPreviewList stlFiles={job.stlFiles} />
                            </Box>
                        )}
                        <Box>
                            {job.status === MillingJobStatus.CREATED ? (
                                <IconButton onClick={() => handleDeleteClick(job._id as string)}>
                                    <DeleteIcon />
                                </IconButton>
                            ) : (
                                <Tooltip
                                    title={<span>{translate('milling-jobs:CANT_DELETE_ACTIVE_MILLING_JOB')}</span>}
                                >
                                    <IconButton>
                                        <DeleteIcon color="disabled" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Box>
                            <IconButton color="primary" href={job.zipFileKey} disabled={!job.zipFileKey}>
                                <DownloadIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>
            <ConfirmDialog
                open={deleteJobConfirmDialogOpen || false}
                handleClose={handleCloseDialog}
                dialogTitle={translate('milling-jobs:DELETE_JOB')}
                dialogContent={translate('milling-jobs:CONFIRM_DELETE_JOB')}
                handleConfirm={handleConfirmDelete}
            />
            <ConfirmDialog
                open={warrantyConfirmDialogOpen || false}
                handleClose={handleCloseDialog}
                dialogTitle={translate('milling-jobs:CREATE_WARRANTY_JOB')}
                dialogContent={translate('milling-jobs:CONFIRM_WARRANTY_JOB')}
                handleConfirm={handleConfirmCreateWarranty}
            />
        </Fragment>
    );
};

export default MillingJobRow;
